/**
 * businessSnapshot — the data layer for the "My Business" dashboard
 * (home-page band + /my-business page). Aug 24, 2026, Ship 3 of the
 * My Business dashboard plan.
 *
 * ARCHITECTURE: pure functions over fetched arrays (the financialsData
 * pattern), composing the existing single-sources-of-truth. All money math
 * goes through CommissionBoard's groupByPerson — the SAME engine the finance
 * board pays from — so a rep's numbers reconcile to the penny with what
 * Chad/Shannon see. Never re-derive rates or sum deal.rm_commission here.
 *
 * INVARIANTS (violating any of these silently produces wrong money numbers):
 *  I1  Every deals select carries an explicit or(owner_id.eq.X,split_rm_id.eq.X)
 *      (or .in list). RLS is the enforcement layer, NEVER the scoping layer —
 *      deals_rm_crew_read leaks other reps' play-pool deals into an RM's
 *      RLS-visible set, and TL/ASM read the whole company.
 *  I2  "My earnings" = the person's CommLineItem amounts from groupByPerson.
 *      Splits pay halves at each RM's own tier rate; non-collect + canvasser
 *      fees exist only as line-item fees.
 *  I3  Field roles (RM/TL/ASM) never see supplement dollars. Invoice-level $
 *      shown to them runs through sanitizeDealForField(), which zeroes the
 *      supplement columns AND final_invoice_amount (final includes supps and
 *      backlogStats/contracted() prefers it — the top silent-leak risk).
 *      Commission numbers are supplement-free by construction (commissionable
 *      backs supps out per scopeCalc).
 *  I4  Real timestamptz columns (commission_paid_at, claimed_at, …) bucket
 *      through etDay()/etMonth(). Fake-UTC ET-wall-clock schedule fields
 *      (adjuster_meeting_date, …) bucket via String(ts).slice(0,10) ONLY.
 *  I5  Never read deals.days_in_stage (frozen) — use lib/dealAge.
 *  I6  Any select that can exceed ~1,000 rows pages via .range() (PostgREST
 *      truncates silently).
 */
import { supabase } from "./supabase";
import type { Deal, TeamMember } from "./types";
import {
  payHoldReason,
  type CommissionDeal,
  type CommLineItem,
  type CommType,
  type PersonGroup,
} from "../components/commissions/CommissionBoard";
import {
  FIN_DEAL_SELECT,
  backlogStats,
  claimsAgg,
  num,
  type BacklogStats,
  type ClaimRow,
  type FinDeal,
} from "./financialsData";
import { calcScope } from "./scopeCalc";
import { daysInStage } from "./dealAge";
import { etDate, mondayOf } from "./weeklySpiffs";
import type { BlocksJson } from "./commitmentMath";
import type { WallStat } from "../components/crew/crewQueries";

// ── Row shape + select contract ───────────────────────────────────────────

/** FIN_DEAL_SELECT already extends the 60-col COMMISSION_DEAL_SELECT, so
 *  groupByPerson/backlogStats accept these rows unchanged. The lead-source
 *  classifier columns (source/referred_by/…) aren't on the Deal type yet —
 *  added via intersection, the FinDeal lead_source pattern. */
export type SnapshotDeal = FinDeal &
  Pick<Deal, "phase" | "adjuster_meeting_date" | "claim_called_in_date" | "canvasser_name"> & {
    source?: string | null; // 'imported'|'canvas'|'manual'|'sr'|'recanvass'
    referred_by?: string | null;
    referral_source_deal_id?: string | null;
    recanvass_set_at?: string | null;
  };

export const SNAPSHOT_DEAL_SELECT =
  FIN_DEAL_SELECT + ",phase,adjuster_meeting_date,claim_called_in_date," +
  "source,canvasser_name,referred_by,referral_source_deal_id,recanvass_set_at";

/** Stages a live pipeline excludes. closed_won included — a won deal is past
 *  the pipeline (its money lives in the commission queue by then). */
export const SNAPSHOT_TERMINAL_STAGES = new Set([
  "closed_lost", "cancelled", "no_thanks", "insufficient_damage",
  "closed_inspection", "claims_denied", "closed_won",
]);

// ── ET period helpers (I4) ────────────────────────────────────────────────
// crew_daily_points is ET-bucketed; commission *_at anchors are timestamptz
// (commission_ready_date is a DATE — mixed anchors go through anchorDay) —
// everything buckets through these so all cards agree at day boundaries.

/** ET calendar day of a REAL timestamptz. NEVER use on fake-UTC fields. */
export function etDay(ts: string): string {
  return etDate(new Date(ts));
}

/** ET "YYYY-MM" of a REAL timestamptz. */
export function etMonth(ts: string): string {
  return etDay(ts).slice(0, 7);
}

/**
 * Day of a MIXED anchor: timestamptz → ET day; a bare DATE string ("2026-08-01",
 * e.g. commission_ready_date, a Postgres DATE) is already a calendar day —
 * running it through new Date() would parse UTC midnight and render the PRIOR
 * ET day (the documented May 9 date-only trap). Audit fix, Aug 24.
 */
export function anchorDay(anchor: string): string {
  return anchor.length <= 10 ? anchor : etDay(anchor);
}
export function anchorMonth(anchor: string): string {
  return anchorDay(anchor).slice(0, 7);
}

/** Today's ET day / month. */
export function todayET(): string {
  return etDate(new Date());
}

/** The last `n` ET months, oldest first, ending with the current month. */
export function lastMonthsET(n: number): string[] {
  const [y, m] = todayET().slice(0, 7).split("-").map(Number);
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m - 1 - i, 15));
    out.push(d.toISOString().slice(0, 7));
  }
  return out;
}

export { mondayOf };

// ── Role scoping + the supplement gate (I3) ───────────────────────────────

export const FIELD_ROLES = new Set(["restore_manager", "team_lead", "asm"]);

/** Mirrors ScopeSection's gate: who may see supplement dollars. */
export function canSeeSupplements(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "admin" || role === "sales_director" || role === "finance";
}

/**
 * Strip supplement dollars from a deal before ANY invoice-level math shown to
 * a field role. Zeroes final_invoice_amount too — it already includes
 * supplements and backlogStats' contracted() prefers it over calcScope, so
 * sanitizing only the supplement columns would still leak (I3).
 */
export function sanitizeDealForField<T extends SnapshotDeal>(d: T): T {
  return {
    ...d,
    supplements: 0,
    supplement_invoice_amount: 0,
    supplement_approved_amount: 0,
    final_invoice_amount: null,
  };
}

/** Invoice-level $ safe for the viewer (job value, not commission). */
export function displayDealValue(d: SnapshotDeal, canSeeSupp: boolean): number {
  if (canSeeSupp) {
    const f = num(d.final_invoice_amount);
    if (f > 0) return f;
    return Math.max(0, calcScope(d).initialInvoice);
  }
  return Math.max(0, calcScope(sanitizeDealForField(d)).initialInvoice);
}

/**
 * Whose deals the viewer's money view covers. RM: self. TL: self + RMs whose
 * team_lead_id points at them. ASM: self + everyone whose asm_id or
 * team_lead_id points at them (their override lines live on those deals).
 * Same rule as MyCommissionsTab.teamIds, plus self.
 */
export function moneyScopeIds(member: TeamMember, roster: TeamMember[]): string[] {
  const ids = new Set<string>([member.id]);
  if (member.role === "team_lead" || member.role === "asm") {
    for (const m of roster) {
      if (m.id === member.id) continue;
      if (m.team_lead_id === member.id) ids.add(m.id);
      if (member.role === "asm" && m.asm_id === member.id) ids.add(m.id);
    }
  }
  return [...ids];
}

// ── Loaders ───────────────────────────────────────────────────────────────

/** Page through PostgREST's ~1,000-row cap (I6). */
async function fetchAllRows<T>(
  ranged: (from: number, to: number) => PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await ranged(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as T[];
    out.push(...rows);
    if (rows.length < PAGE) break;
    if (from > 20_000) break; // runaway guard
  }
  return out;
}

export interface SnapshotDeals {
  /** Pull A — the commission queue/history spine (commission_ready_date set). */
  queue: SnapshotDeal[];
  /** Pull B — the live pipeline spine (non-terminal stages). */
  live: SnapshotDeal[];
}

/**
 * The two deal pulls every money card derives from. Explicit owner/split
 * scoping per I1 — never rely on RLS to mean "my deals".
 */
export async function fetchSnapshotDeals(scopeIds: string[]): Promise<SnapshotDeals> {
  const inList = `(${scopeIds.join(",")})`;
  const scopeOr = `owner_id.in.${inList},split_rm_id.in.${inList}`;
  const terminal = `(${[...SNAPSHOT_TERMINAL_STAGES].join(",")})`;
  const [queue, live] = await Promise.all([
    fetchAllRows<SnapshotDeal>((from, to) =>
      supabase.from("deals").select(SNAPSHOT_DEAL_SELECT)
        .not("commission_ready_date", "is", null)
        .or(scopeOr)
        .order("commission_ready_date", { ascending: true })
        .order("id", { ascending: true }) // tie-break: paging with tied keys dup/skips at page boundaries
        .range(from, to),
    ),
    fetchAllRows<SnapshotDeal>((from, to) =>
      supabase.from("deals").select(SNAPSHOT_DEAL_SELECT)
        .not("stage", "in", terminal)
        .or(scopeOr)
        .order("created_at", { ascending: true })
        .order("id", { ascending: true })
        .range(from, to),
    ),
  ]);
  return { queue, live };
}

/**
 * Canvasser attribution (who gets the 5-point carve-out) lives in
 * lib/canvasserCredit so every money surface reads one rule. Re-exported
 * here for the callers that already import it from this module.
 */
export { fetchCanvasserByDeal } from "./canvasserCredit";

/** Outstanding draw balance + pending request count (extracted verbatim from
 *  MyCommissionsTab so the two can't drift; commission_draws is self-read RLS). */
export async function fetchDrawBalance(memberId: string): Promise<{ balance: number; pendingCount: number }> {
  const [drawsRes, repaysRes] = await Promise.all([
    supabase.from("commission_draws").select("id, approved_amount, paid_at, approval_status").eq("team_member_id", memberId),
    supabase.from("commission_draw_repayments").select("draw_id, amount"),
  ]);
  const myDraws = (drawsRes.data ?? []) as Array<{ id: string; approved_amount: number | null; paid_at: string | null; approval_status: string }>;
  const myDrawIds = new Set(myDraws.map((d) => d.id));
  const repaidByDraw: Record<string, number> = {};
  for (const r of (repaysRes.data ?? []) as Array<{ draw_id: string; amount: number }>) {
    if (myDrawIds.has(r.draw_id)) repaidByDraw[r.draw_id] = (repaidByDraw[r.draw_id] || 0) + Number(r.amount);
  }
  const balance = myDraws
    .filter((d) => d.approval_status === "approved" && d.paid_at)
    .reduce((s, d) => s + (Number(d.approved_amount) || 0) - (repaidByDraw[d.id] || 0), 0);
  return { balance: Math.max(0, balance), pendingCount: myDraws.filter((d) => d.approval_status === "pending").length };
}

export interface SpiffHistoryRow { name: string; amount: number; claimed_at: string; paid: boolean }

/** SPIFFs won, from the spiffs TABLE — the awarded source of truth for $
 *  history (computeSpiffSummary is only for the live current-week chase). */
export async function fetchMySpiffs(userId: string, sinceISO: string): Promise<SpiffHistoryRow[]> {
  const { data, error } = await supabase
    .from("spiffs")
    .select("name, amount_cents, claimed_at, paid_at")
    .eq("user_id", userId)
    .gte("claimed_at", sinceISO)
    .order("claimed_at", { ascending: false });
  if (error) return [];
  return (data ?? []).map((r: { name: string; amount_cents: number | string | null; claimed_at: string; paid_at: string | null }) => ({
    name: r.name,
    amount: Number(r.amount_cents ?? 0) / 100,
    claimed_at: r.claimed_at,
    paid: !!r.paid_at,
  }));
}

/** The rep's weekly_commitments row for a week — powers the Plan-changed sheet. */
export interface CommitmentRowLite {
  id: string;
  blocks: BlocksJson;
  week_start: string;
  locked_at: string | null;
}
export async function fetchMyCommitmentRow(userId: string, weekStart: string): Promise<CommitmentRowLite | null> {
  const { data } = await supabase
    .from("weekly_commitments")
    .select("id, blocks, week_start, locked_at")
    .eq("user_id", userId)
    .eq("week_start", weekStart)
    .maybeSingle();
  return (data as CommitmentRowLite | null) ?? null;
}

// ── PAST — earnings (pure, over groupByPerson output) ─────────────────────

/** The viewer's own line items out of a groupByPerson run (I2). */
export function myLines(groups: PersonGroup[], personId: string): CommLineItem[] {
  const mine = groups.find((g) => g.personId === personId);
  return (mine?.typeGroups ?? []).flatMap((t) => t.items);
}

export interface EarningsMonthRow {
  month: string; // ET "YYYY-MM"
  paid: number;
  approved: number; // approved-not-yet-paid, anchored on approval month
  byType: Partial<Record<CommType, number>>;
  paidDealIds: string[];
}

export interface EarningsSummary {
  rows: EarningsMonthRow[]; // oldest → newest, spanning `months`
  totalPaid: number; // all-time within the fetched set
  totalApprovedAwaiting: number;
  avgPerPaidDeal: number | null;
  paidDealCount: number;
}

/**
 * Monthly earnings series for one person. Paid lines anchor on the deal's
 * commission_paid_at ET month; approved-not-paid anchor on approval month.
 * A split deal counts once at the person's half — the truthful "what I made
 * per deal I touched".
 */
export function earningsByMonth(groups: PersonGroup[], personId: string, months = 6): EarningsSummary {
  const monthKeys = lastMonthsET(months);
  const byMonth = new Map<string, EarningsMonthRow>(
    monthKeys.map((m) => [m, { month: m, paid: 0, approved: 0, byType: {}, paidDealIds: [] }]),
  );
  const mine = groups.find((g) => g.personId === personId);
  let totalPaid = 0;
  let totalApprovedAwaiting = 0;
  const paidDeals = new Set<string>();
  for (const tg of mine?.typeGroups ?? []) {
    for (const it of tg.items) {
      const d = it.deal;
      if (d.commission_paid) {
        totalPaid += it.amount;
        paidDeals.add(d.id);
        // A stored ledger line carries its own payout date (a 5★ true-up pays
        // after the deal's commission_paid_at); legacy lines use the deal's.
        const anchor = it.paidAt ?? d.commission_paid_at ?? d.commission_ready_date;
        if (anchor) {
          const row = byMonth.get(anchorMonth(String(anchor)));
          if (row) {
            row.paid += it.amount;
            row.byType[tg.type] = (row.byType[tg.type] ?? 0) + it.amount;
            row.paidDealIds.push(d.id);
          }
        }
      } else if (d.commission_approved) {
        totalApprovedAwaiting += it.amount;
        const anchor = d.commission_approved_at ?? d.commission_ready_date;
        if (anchor) {
          const row = byMonth.get(anchorMonth(String(anchor)));
          if (row) row.approved += it.amount;
        }
      }
    }
  }
  return {
    rows: monthKeys.map((m) => byMonth.get(m)!),
    totalPaid,
    totalApprovedAwaiting,
    avgPerPaidDeal: paidDeals.size ? totalPaid / paidDeals.size : null,
    paidDealCount: paidDeals.size,
  };
}

export interface ClosedMonthGroup {
  month: string; // ET "YYYY-MM"
  total: number;
  deals: Array<{ deal: CommissionDeal; myTotal: number }>;
}

/**
 * "Closed & built" history — every deal the person has been PAID on, grouped
 * by ET payout month, newest first. This is the closed-deals-vanish fix: the
 * pipeline hides closing/closed stages from field roles, so this list is
 * where a rep looks backward.
 */
export function closedDealsByMonth(groups: PersonGroup[], personId: string): ClosedMonthGroup[] {
  const byMonth = new Map<string, ClosedMonthGroup>();
  const perDeal = new Map<string, { deal: CommissionDeal; myTotal: number; month: string }>();
  for (const it of myLines(groups, personId)) {
    const d = it.deal;
    if (!d.commission_paid) continue;
    const anchor = d.commission_paid_at ?? d.commission_ready_date;
    if (!anchor) continue;
    const month = anchorMonth(String(anchor));
    const e = perDeal.get(d.id) ?? { deal: d, myTotal: 0, month };
    e.myTotal += it.amount;
    perDeal.set(d.id, e);
  }
  for (const e of perDeal.values()) {
    const g = byMonth.get(e.month) ?? { month: e.month, total: 0, deals: [] };
    g.total += e.myTotal;
    g.deals.push({ deal: e.deal, myTotal: e.myTotal });
    byMonth.set(e.month, g);
  }
  return [...byMonth.values()]
    .map((g) => ({ ...g, deals: g.deals.sort((a, b) => b.myTotal - a.myTotal) }))
    .sort((a, b) => b.month.localeCompare(a.month));
}

export interface HeldCommission { deal: CommissionDeal; reason: string; myPendingAmount: number }

/** Pending lines whose payout is blocked (credits ≥ $501 hold, etc.). */
export function heldCommissions(groups: PersonGroup[], personId: string): HeldCommission[] {
  const out: HeldCommission[] = [];
  const seen = new Set<string>();
  const mine = groups.find((g) => g.personId === personId);
  for (const tg of mine?.typeGroups ?? []) {
    for (const it of tg.items) {
      const d = it.deal;
      if (d.commission_paid || d.commission_approved) continue;
      const reason = payHoldReason(d);
      if (!reason) continue;
      if (seen.has(d.id)) {
        const row = out.find((r) => r.deal.id === d.id);
        if (row) row.myPendingAmount += it.amount;
        continue;
      }
      seen.add(d.id);
      out.push({ deal: d, reason, myPendingAmount: it.amount });
    }
  }
  return out;
}

// ── FUTURE — outlook ──────────────────────────────────────────────────────

export interface CommissionOutlook {
  pendingQueue: number; // my line $ in the pending bucket (awaiting Chad)
  pendingDeals: number;
  approvedAwaitingPay: number;
  approvedDeals: number;
  /** ESTIMATE — signed-unbuilt deals not yet in the commission queue,
   *  run through the same groupByPerson engine. Label it "projected". */
  projectedSignedUnbuilt: number;
  projectedDeals: Array<{ deal: SnapshotDeal; myEstimate: number }>;
}

/** Split the queue groups into pending/approved by the boolean flags
 *  (mirrors MyCommissionsTab bucketing exactly) + project the signed-unbuilt
 *  pre-queue book. */
export function commissionOutlook(
  queueGroups: PersonGroup[],
  projectionGroups: PersonGroup[],
  projectionDealsById: Map<string, SnapshotDeal>,
  personId: string,
): CommissionOutlook {
  let pendingQueue = 0, approvedAwaitingPay = 0;
  const pendingDealIds = new Set<string>(), approvedDealIds = new Set<string>();
  for (const it of myLines(queueGroups, personId)) {
    const d = it.deal;
    if (d.commission_paid) continue;
    if (d.commission_approved) { approvedAwaitingPay += it.amount; approvedDealIds.add(d.id); }
    else { pendingQueue += it.amount; pendingDealIds.add(d.id); }
  }
  const projected = new Map<string, { deal: SnapshotDeal; myEstimate: number }>();
  for (const it of myLines(projectionGroups, personId)) {
    const deal = projectionDealsById.get(it.deal.id);
    if (!deal) continue;
    const e = projected.get(deal.id) ?? { deal, myEstimate: 0 };
    e.myEstimate += it.amount;
    projected.set(deal.id, e);
  }
  const projectedDeals = [...projected.values()].sort((a, b) => b.myEstimate - a.myEstimate);
  return {
    pendingQueue,
    pendingDeals: pendingDealIds.size,
    approvedAwaitingPay,
    approvedDeals: approvedDealIds.size,
    projectedSignedUnbuilt: projectedDeals.reduce((s, p) => s + p.myEstimate, 0),
    projectedDeals,
  };
}

/** The signed-unbuilt, pre-commission-queue subset of the live pull — the
 *  projection set fed to groupByPerson. */
export function projectionSet(live: SnapshotDeal[]): SnapshotDeal[] {
  return live.filter(
    (d) => d.install_agreement_signed === true && !d.build_completed_date && !d.commission_ready_date,
  );
}

/** backlogStats over the viewer-safe deal list (I3: sanitize for field roles). */
export function myBacklog(live: SnapshotDeal[], canSeeSupp: boolean): BacklogStats {
  const rows = canSeeSupp ? live : live.map(sanitizeDealForField);
  return backlogStats(rows);
}

// ── PRESENT — pipeline + activity ─────────────────────────────────────────

export interface PipelineSummary {
  liveCount: number;
  /** Σ estimated job value across live deals with a scoped value (>0). Field
   *  roles get the supplement-free figure (I3). */
  bookValue: number;
  scopedCount: number;
  byPhase: Array<{ phase: string; count: number; value: number }>;
}

export function pipelineSummary(live: SnapshotDeal[], canSeeSupp: boolean): PipelineSummary {
  const byPhase = new Map<string, { phase: string; count: number; value: number }>();
  let bookValue = 0, scopedCount = 0;
  for (const d of live) {
    const v = displayDealValue(d, canSeeSupp);
    if (v > 0) { bookValue += v; scopedCount++; }
    const key = String(d.phase ?? "other");
    const e = byPhase.get(key) ?? { phase: key, count: 0, value: 0 };
    e.count++; e.value += v;
    byPhase.set(key, e);
  }
  return { liveCount: live.length, bookValue, scopedCount, byPhase: [...byPhase.values()] };
}

export interface ColdDeal { deal: SnapshotDeal; days: number }

/** Live deals sitting in their stage ≥ threshold days (dealAge, never the
 *  frozen days_in_stage column — I5). */
export function coldDeals(live: SnapshotDeal[], thresholdDays = 14): ColdDeal[] {
  const out: ColdDeal[] = [];
  for (const d of live) {
    const days = daysInStage(d);
    if (days != null && days >= thresholdDays) out.push({ deal: d, days });
  }
  return out.sort((a, b) => b.days - a.days);
}

// ── Funnel + shift summaries ──────────────────────────────────────────────

export interface PersonalFunnel {
  apps: number; claims: number; signedIA: number; builds: number;
  claimPerApp: number | null; signPerClaim: number | null; buildPerSign: number | null;
}

/** Off crew_daily_points (fractions kept — split credit is 0.5 each). */
export function personalFunnel(w: WallStat | undefined): PersonalFunnel {
  const apps = w?.apps ?? 0, claims = w?.claims ?? 0;
  const signedIA = w?.builds_scheduled ?? 0, builds = w?.builds ?? 0;
  const ratio = (a: number, b: number) => (b > 0 ? a / b : null);
  return {
    apps, claims, signedIA, builds,
    claimPerApp: ratio(claims, apps),
    signPerClaim: ratio(signedIA, claims),
    buildPerSign: ratio(builds, signedIA),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// LENSES — time ↔ money (Ship 5)
// ═══════════════════════════════════════════════════════════════════════════

// ── (a) Geography ─────────────────────────────────────────────────────────

export interface CityEffortRow { city: string; knocks: number; contacts: number; appts: number; active_days: number }

/** Per-city knock effort via the canvas_city_effort RPC (SECURITY INVOKER;
 *  knock activity is public within the field team). ET-midnight bounds —
 *  the fetchSpiffAudit convention. */
export async function fetchCityEffort(userId: string, fromISO: string, toISO: string): Promise<CityEffortRow[]> {
  const { data, error } = await supabase.rpc("canvas_city_effort", {
    p_user: userId,
    p_from: `${fromISO}T00:00:00-04:00`,
    p_to: `${toISO}T23:59:59-04:00`,
  });
  if (error) return [];
  return (data ?? []) as CityEffortRow[];
}

const normCity = (raw: string | null | undefined): string => {
  const t = (raw ?? "").trim();
  if (!t) return "Unknown";
  return t.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

export interface CityLensRow {
  city: string;
  knocks: number; contacts: number; appts: number; activeDays: number;
  myPaid: number; myProjected: number; dealsSigned: number;
}

/** Money joins on deal.property_city; effort on knock_pins.property_city —
 *  both normalized. "You knock Woodstock but your money comes from Canton." */
export function cityLens(
  effort: CityEffortRow[],
  paidLines: CommLineItem[],
  projLines: CommLineItem[],
  signedDeals: SnapshotDeal[],
): CityLensRow[] {
  const rows = new Map<string, CityLensRow>();
  const get = (city: string) => {
    const key = normCity(city);
    const e = rows.get(key) ?? { city: key, knocks: 0, contacts: 0, appts: 0, activeDays: 0, myPaid: 0, myProjected: 0, dealsSigned: 0 };
    rows.set(key, e);
    return e;
  };
  for (const r of effort) {
    const e = get(r.city);
    e.knocks += r.knocks; e.contacts += r.contacts; e.appts += r.appts; e.activeDays += r.active_days;
  }
  for (const it of paidLines) {
    if (!it.deal.commission_paid) continue;
    get((it.deal as SnapshotDeal).property_city ?? "").myPaid += it.amount;
  }
  for (const it of projLines) get((it.deal as SnapshotDeal).property_city ?? "").myProjected += it.amount;
  for (const d of signedDeals) get(d.property_city ?? "").dealsSigned += 1;
  return [...rows.values()].sort((a, b) => (b.myPaid + b.myProjected) - (a.myPaid + a.myProjected) || b.knocks - a.knocks);
}

// ── (b) Lead source ───────────────────────────────────────────────────────

export type LeadSourceBucket = "canvass" | "company_lead" | "referral" | "recanvass" | "self_gen" | "other";

export const LEAD_SOURCE_LABELS: Record<LeadSourceBucket, string> = {
  canvass: "🚪 Canvassing / SR",
  company_lead: "🏢 Company lead",
  referral: "🤝 Referral",
  recanvass: "📞 Re-canvass",
  self_gen: "⭐ Self-generated",
  other: "❔ Other / unknown",
};

/**
 * deals.lead_source is free text (100+ person names, casing chaos) — NEVER
 * groupBy it raw. Classify over the structured columns first (source,
 * referral_source_deal_id, canvasser_name, recanvass_set_at), then bucket
 * the residual text honestly, with roster names resolved to canvass vs
 * self-gen by the named person's role.
 */
export function classifyLeadSource(
  d: SnapshotDeal,
  rosterByName: Map<string, string>, // lower(full_name) → role
): LeadSourceBucket {
  const ls = (d.lead_source ?? "").trim().toLowerCase();
  if (d.referral_source_deal_id || d.referred_by || /referr/.test(ls)) return "referral";
  if (d.source === "recanvass" || d.recanvass_set_at) return "recanvass";
  if (d.source === "canvas" || d.source === "sr" || d.canvasser_name || /sales ?rabbit/.test(ls)) return "canvass";
  const namedRole = rosterByName.get(ls);
  if (namedRole === "canvasser") return "canvass";
  if (namedRole) return "self_gen";
  if (/^(open|admin|call ?in|called ?in|website|company|onboarding|office|savannah)/.test(ls)) return "company_lead";
  if (/restore manager|self/.test(ls)) return "self_gen";
  return "other";
}

export interface LeadSourceRow {
  bucket: LeadSourceBucket;
  deals: number;
  signed: number;
  signRate: number | null;
  myPaid: number;
  paidDeals: number;
  avgPerPaidDeal: number | null;
}

export function leadSourceLens(
  allMyDeals: SnapshotDeal[], // live + queue + terminal — the full book
  paidByDeal: Map<string, number>, // deal id → my paid $
  rosterByName: Map<string, string>,
): LeadSourceRow[] {
  const rows = new Map<LeadSourceBucket, LeadSourceRow>();
  for (const d of allMyDeals) {
    const b = classifyLeadSource(d, rosterByName);
    const e = rows.get(b) ?? { bucket: b, deals: 0, signed: 0, signRate: null, myPaid: 0, paidDeals: 0, avgPerPaidDeal: null };
    e.deals += 1;
    if (d.install_agreement_signed === true || d.commission_ready_date) e.signed += 1;
    const paid = paidByDeal.get(d.id);
    if (paid) { e.myPaid += paid; e.paidDeals += 1; }
    rows.set(b, e);
  }
  return [...rows.values()]
    .map((e) => ({
      ...e,
      signRate: e.deals > 0 ? e.signed / e.deals : null,
      avgPerPaidDeal: e.paidDeals > 0 ? e.myPaid / e.paidDeals : null,
    }))
    .sort((a, b) => b.myPaid - a.myPaid || b.deals - a.deals);
}

/** Light pull of the viewer's lost-TERMINAL deals — the win-rate denominators
 *  the live/queue pulls exclude. Best-effort ([] on failure). */
export async function fetchMyTerminalDeals(scopeIds: string[]): Promise<SnapshotDeal[]> {
  const inList = `(${scopeIds.join(",")})`;
  try {
    return await fetchAllRows<SnapshotDeal>((from, to) =>
      supabase.from("deals")
        .select("id,owner_id,split_rm_id,stage,created_at,install_agreement_signed,commission_ready_date,lead_source,source,canvasser_name,referred_by,referral_source_deal_id,recanvass_set_at,property_city")
        .in("stage", [...LOST_TERMINAL])
        .or(`owner_id.in.${inList},split_rm_id.in.${inList}`)
        .order("created_at", { ascending: true })
        .order("id", { ascending: true })
        .range(from, to),
    );
  } catch {
    return [];
  }
}

// ── (c) Deal stages ───────────────────────────────────────────────────────

export interface StageHistoryRow {
  deal_id: string;
  from_stage: string | null;
  to_stage: string | null;
  changed_at: string;
  duration_in_previous_stage: string | null; // Postgres interval text
}

/** "17 days 04:30:00" / "04:30:00" / "1 day" → fractional days. */
export function intervalToDays(iv: string | null): number | null {
  if (!iv) return null;
  let days = 0;
  const dm = iv.match(/(\d+)\s+day/);
  if (dm) days += Number(dm[1]);
  const tm = iv.match(/(\d+):(\d+):(\d+)/);
  if (tm) days += Number(tm[1]) / 24 + Number(tm[2]) / 1440 + Number(tm[3]) / 86400;
  return days;
}

/** First client reader of stage_history (RLS cascades on deal read). Paged. */
export async function fetchStageHistory(dealIds: string[]): Promise<StageHistoryRow[]> {
  const out: StageHistoryRow[] = [];
  for (let i = 0; i < dealIds.length; i += 150) {
    const chunk = dealIds.slice(i, i + 150);
    try {
      const rows = await fetchAllRows<StageHistoryRow>((from, to) =>
        supabase.from("stage_history")
          .select("deal_id,from_stage,to_stage,changed_at,duration_in_previous_stage")
          .in("deal_id", chunk)
          .order("changed_at", { ascending: true })
          .range(from, to),
      );
      out.push(...rows);
    } catch { /* best-effort — a failed chunk degrades the lens, not the page */ }
  }
  return out;
}

const LOST_TERMINAL = new Set(["closed_lost", "cancelled", "no_thanks", "insufficient_damage", "claims_denied", "closed_inspection"]);

export interface StageFlowRow {
  stage: string;
  entered: number;         // times my deals entered this stage, ever
  medianDays: number | null; // median dwell when leaving this stage
  nowSitting: number;      // my live deals currently here
  diedHere: number;        // my deals whose last stop before a lost-terminal was this stage
}

export function stageFlow(history: StageHistoryRow[], live: SnapshotDeal[], terminal: SnapshotDeal[]): StageFlowRow[] {
  const rows = new Map<string, StageFlowRow>();
  const get = (s: string) => {
    const e = rows.get(s) ?? { stage: s, entered: 0, medianDays: null, nowSitting: 0, diedHere: 0 };
    rows.set(s, e);
    return e;
  };
  const dwellByStage = new Map<string, number[]>();
  const lastTransition = new Map<string, StageHistoryRow>();
  for (const h of history) {
    if (h.to_stage) get(h.to_stage).entered += 1;
    if (h.from_stage) {
      const d = intervalToDays(h.duration_in_previous_stage);
      if (d != null) {
        const arr = dwellByStage.get(h.from_stage) ?? [];
        arr.push(d);
        dwellByStage.set(h.from_stage, arr);
      }
    }
    const prev = lastTransition.get(h.deal_id);
    if (!prev || h.changed_at > prev.changed_at) lastTransition.set(h.deal_id, h);
  }
  for (const [stage, arr] of dwellByStage) {
    arr.sort((a, b) => a - b);
    get(stage).medianDays = Math.round(arr[Math.floor(arr.length / 2)] * 10) / 10;
  }
  for (const d of live) if (d.stage) get(String(d.stage)).nowSitting += 1;
  for (const d of terminal) {
    if (!LOST_TERMINAL.has(String(d.stage ?? ""))) continue;
    const last = lastTransition.get(d.id);
    if (last?.from_stage) get(last.from_stage).diedHere += 1;
  }
  return [...rows.values()]
    .filter((r) => r.entered > 0 || r.nowSitting > 0)
    .sort((a, b) => (b.medianDays ?? 0) - (a.medianDays ?? 0));
}

// ── (d) Claim outcomes ────────────────────────────────────────────────────

/** Per-rep claim outcomes via the claim_outcomes_for RPC (invoker — an RM
 *  querying someone else gets []). Shape matches financialsData.ClaimRow so
 *  claimsAgg reuses unchanged. */
export async function fetchMyClaimOutcomes(memberId: string): Promise<ClaimRow[]> {
  const { data, error } = await supabase.rpc("claim_outcomes_for", { p_member: memberId });
  if (error) return [];
  return (data ?? []) as ClaimRow[];
}

export interface CarrierClaimRow { carrier: string; agg: ReturnType<typeof claimsAgg> }

export function claimsByCarrier(rows: ClaimRow[]): CarrierClaimRow[] {
  const byCarrier = new Map<string, ClaimRow[]>();
  for (const r of rows) {
    const k = r.carrier || "(not recorded)";
    (byCarrier.get(k) ?? byCarrier.set(k, []).get(k)!).push(r);
  }
  return [...byCarrier.entries()]
    .map(([carrier, list]) => ({ carrier, agg: claimsAgg(list) }))
    .sort((a, b) => b.agg.c - a.agg.c);
}

export { claimsAgg };

// ── (e) Activity mix ──────────────────────────────────────────────────────

export interface ActivityMix {
  fieldHours: number; knocks: number; contacts: number; appts: number; miles: number;
  callsOnMyDeals: number; textsISent: number;
  apptsPerFieldHour: number | null;
}

/** Head-count comm volume on the viewer's deals (twilio RLS cascades on deal
 *  read; created_by attribution excludes the ~75% automated traffic). */
export async function fetchCommCounts(memberId: string, dealIds: string[], sinceISO: string): Promise<{ calls: number; texts: number }> {
  // Dedupe — queue and live pulls overlap on supps-lifecycle deals, and a
  // repeated id across two chunks would double-count its calls (audit fix).
  dealIds = [...new Set(dealIds)];
  let calls = 0;
  for (let i = 0; i < dealIds.length; i += 200) {
    const chunk = dealIds.slice(i, i + 200);
    const { count } = await supabase.from("twilio_calls")
      .select("id", { count: "exact", head: true })
      .in("deal_id", chunk)
      .gte("started_at", sinceISO);
    calls += count ?? 0;
  }
  const { count: texts } = await supabase.from("twilio_messages")
    .select("id", { count: "exact", head: true })
    .eq("created_by", memberId)
    .eq("direction", "outbound")
    .gte("sent_at", sinceISO);
  return { calls, texts: texts ?? 0 };
}

export function activityMix(shift: ShiftSum, calls: number, texts: number): ActivityMix {
  return {
    fieldHours: Math.round(shift.hours * 10) / 10,
    knocks: shift.knocks,
    contacts: shift.contacts,
    appts: shift.appts,
    miles: Math.round(shift.miles * 10) / 10,
    callsOnMyDeals: calls,
    textsISent: texts,
    apptsPerFieldHour: shift.hours > 0 ? Math.round((shift.appts / shift.hours) * 100) / 100 : null,
  };
}

export interface ShiftSum { shifts: number; hours: number; knocks: number; contacts: number; appts: number; miles: number }

export interface AuditShiftRow {
  hours_worked: number; knocks: number; contacts: number; appts: number;
  claims: number; miles_walked: number | null;
}

export function sumShifts(rows: AuditShiftRow[]): ShiftSum {
  return rows.reduce<ShiftSum>(
    (s, r) => ({
      shifts: s.shifts + 1,
      hours: s.hours + Number(r.hours_worked ?? 0),
      knocks: s.knocks + Number(r.knocks ?? 0),
      contacts: s.contacts + Number(r.contacts ?? 0),
      appts: s.appts + Number(r.appts ?? 0),
      miles: s.miles + Number(r.miles_walked ?? 0),
    }),
    { shifts: 0, hours: 0, knocks: 0, contacts: 0, appts: 0, miles: 0 },
  );
}

// ── TL/ASM team snapshot (Ship 6) ─────────────────────────────────────────

export interface TeamMemberRow {
  id: string;
  name: string;
  role: string;
  /** This week's production (crew_daily_points; fractions kept). */
  apps: number; claims: number; signedIA: number; builds: number;
  /** The viewer's own override money on this member's deals. */
  myOverridePaid: number;
  myOverridePending: number;
  myOverrideProjected: number;
}

/**
 * Per-member team view for a TL/ASM: production (already public on the crew
 * wall) + the VIEWER'S override attributable to each member's deals. Never
 * another rep's personal dollars — money stays private-to-self.
 */
export function teamSnapshot(
  queueGroups: PersonGroup[],
  projGroups: PersonGroup[],
  personId: string,
  teamIds: string[],
  members: Record<string, TeamMember>,
  wallStats: Record<string, WallStat>,
): TeamMemberRow[] {
  const rows = new Map<string, TeamMemberRow>();
  for (const id of teamIds) {
    if (id === personId) continue;
    const m = members[id];
    const w = wallStats[id];
    rows.set(id, {
      id,
      name: m?.full_name ?? "—",
      role: m?.role ?? "",
      apps: w?.apps ?? 0,
      claims: w?.claims ?? 0,
      signedIA: w?.builds_scheduled ?? 0,
      builds: w?.builds ?? 0,
      myOverridePaid: 0,
      myOverridePending: 0,
      myOverrideProjected: 0,
    });
  }
  // Oct 29, 2026: a trade upside pool line (CommType "trade_upside") on a deal
  // the viewer does not hold is a share Chad carved them out of a team
  // member's pool — override money, as My Commissions counts it. On a deal the
  // viewer holds (owner or split RM) it is their own pay and stays out.
  const isOverride = (type: string, it: CommLineItem) =>
    type === "tl_override" || type === "asm_override" ||
    (type === "trade_upside" && it.deal.owner_id !== personId && it.deal.split_rm_id !== personId);
  const overrideLines = (groups: PersonGroup[]) =>
    (groups.find((g) => g.personId === personId)?.typeGroups ?? [])
      .flatMap((t) => t.items.filter((it) => isOverride(t.type as string, it)));
  // Attribute to the team member whose half generated the line: the owner
  // when they're on this team, else the split RM (the Jul 21 per-half rule
  // pays a TL on their RM's SPLIT half of a cross-team deal — owner-only
  // attribution silently dropped those lines; audit fix, Aug 24).
  const rowFor = (d: CommissionDeal) =>
    (d.owner_id ? rows.get(d.owner_id) : undefined) ??
    (d.split_rm_id ? rows.get(d.split_rm_id) : undefined);
  for (const it of overrideLines(queueGroups)) {
    const row = rowFor(it.deal);
    if (!row) continue;
    if (it.deal.commission_paid) row.myOverridePaid += it.amount;
    else row.myOverridePending += it.amount;
  }
  for (const it of overrideLines(projGroups)) {
    const row = rowFor(it.deal);
    if (row) row.myOverrideProjected += it.amount;
  }
  return [...rows.values()].sort(
    (a, b) => (b.myOverridePaid + b.myOverridePending) - (a.myOverridePaid + a.myOverridePending) || b.claims - a.claims,
  );
}
